import React, { useMemo, useState } from 'react';
import { Chart, Geom, Axis, Tooltip } from 'bizcharts';
import { Checkbox, Empty } from 'antd';
import './SiteSpeed.less';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

const chartEntriesColors = ['#4BB2C5', '#0A901B', '#E67E22', '#C1380A', '#722D8E', '#33CCC8'];

function getScale(chartData: ChartDataType[]) {
    const chartTickCount = getTickCount(chartData);

    const scale: any = {
        value: {
            type: 'linear',
            formatter: (val: number) => (val / 1000).toFixed(2),
            tickCount: 5,
        },
        c_at: {
            type: 'timeCat',
            mask: 'MMM DD',
            tickCount: chartTickCount,
        },
    };

    if (chartTickCount > 1) {
        scale.c_at.range = [0, 1];
    }
    return scale;
}

function getTickCount(chartData: ChartDataType[]) {
    const dataEntriesCountForRegions: Record<string, number> = {};

    chartData.forEach((dataEntry) => {
        if (!(dataEntry.region in dataEntriesCountForRegions)) {
            dataEntriesCountForRegions[dataEntry.region] = 1;
        } else {
            dataEntriesCountForRegions[dataEntry.region] += 1;
        }
    });

    const arrayOfEntriesCount = Object.values(dataEntriesCountForRegions);

    if (arrayOfEntriesCount.length === 0) {
        return 0;
    }

    return Math.max(...arrayOfEntriesCount);
}

type ChartDataType = {
    c_at: string;
    region: string;
    value: number;
};

type Props = {
    chartData: ChartDataType[];
    regions: string[];
};

export default function SiteSpeedChart({ chartData, regions }: Props) {
    const [hiddenRegions, setHiddenRegions] = useState<string[]>([]);

    const handleCheckboxChange = (event: CheckboxChangeEvent) => {
        const targetRegion = event.target.name;
        const isChecked = event.target.checked;

        if (targetRegion) {
            if (isChecked) {
                setHiddenRegions([...hiddenRegions].filter((hiddenRegion) => hiddenRegion !== targetRegion));
            } else {
                setHiddenRegions([...hiddenRegions, targetRegion]);
            }
        }
    };

    const getColorForRegion = (region?: string) => {
        return chartEntriesColors[regions.findIndex((_region) => _region === region)];
    };

    const handleTooltipChange = (event: { tooltip: any; x: number; y: number; items: any }) => {
        const tooltipItems = [...event.items];

        tooltipItems.forEach((item) => {
            item.value = `${(item.point._origin.value / 1000).toFixed(1)} s`;
        });
    };

    const regionsWithData = useMemo(() => regions.filter((region) => chartData.some((dataEntry) => dataEntry.region === region)), [chartData, regions]);
    const filteredChartData = useMemo(() => chartData.filter((dataPoint) => !hiddenRegions.includes(dataPoint.region)), [chartData, hiddenRegions]);
    const scale = useMemo(() => getScale(filteredChartData), [filteredChartData]);

    return (
        <div className="loading-time-chart custom-scrollbar">
            {regionsWithData.length > 1 &&
                regionsWithData.map((region) => (
                    <Checkbox
                        className="site-speed-checkbox"
                        style={{
                            // @ts-ignore used in css styles
                            '--background-color': getColorForRegion(region),
                            '--border-color': getColorForRegion(region),
                        }}
                        key={region}
                        name={region}
                        checked={!hiddenRegions.includes(region)}
                        onChange={handleCheckboxChange}
                    >
                        {region}
                    </Checkbox>
                ))}

            {filteredChartData.length > 0 && (
                <Chart
                    padding="auto"
                    height={340}
                    data={filteredChartData}
                    scale={scale}
                    animate={false}
                    forceFit
                    onTooltipChange={handleTooltipChange}
                >
                    <Axis name="c_at" />
                    <Axis name="value" />
                    <Tooltip />
                    <Geom
                        type="line"
                        position="c_at*value"
                        size={2}
                        color={['region', getColorForRegion]}
                        shape={'smooth'}
                    />
                    <Geom
                        type="point"
                        position="c_at*value"
                        size={4}
                        shape={'circle'}
                        color={['region', getColorForRegion]}
                        style={{ lineWidth: 1 }}
                    />
                </Chart>
            )}

            {filteredChartData.length === 0 && (
                <Empty
                    className="empty-state"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No data available yet"
                />
            )}
        </div>
    );
}
